// @ts-nocheck
import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import type { Session } from '@supabase/supabase-js';
import type { Profile } from '../../types';
import { AdminConfirmDialog } from './AdminConfirmDialog';
import { useAdminToast } from './useAdminToast';

interface AdminSettingsProps {
  session: Session;
  profile: Profile | null;
}

export const AdminSettings: React.FC<AdminSettingsProps> = ({ session, profile }) => {
  const [autoProcessingStatus, setAutoProcessingStatus] = useState<{
    enabled: boolean;
    available: boolean;
    loading: boolean;
  }>({
    enabled: false,
    available: true,
    loading: true,
  });
  const [manualProcessing, setManualProcessing] = useState(false);
  const [lastManualRun, setLastManualRun] = useState<string | null>(null);
  const [showToggleDialog, setShowToggleDialog] = useState(false);
  const [pendingToggle, setPendingToggle] = useState<boolean | null>(null);
  const { showSuccess, showError } = useAdminToast();

  useEffect(() => {
    checkAutoProcessingStatus();
  }, []);

  const checkAutoProcessingStatus = async () => {
    setAutoProcessingStatus((prev) => ({ ...prev, loading: true }));
    try {
      const { data, error } = await supabase.functions.invoke('setup-auto-processing', {
        body: { action: 'status' },
      });

      if (error) throw error;

      setAutoProcessingStatus({
        enabled: data?.enabled || false,
        available: data?.available !== false,
        loading: false,
      });
    } catch (err) {
      console.error('Error checking auto-processing status:', err);
      setAutoProcessingStatus((prev) => ({ ...prev, loading: false }));
    }
  };

  const handleToggleClick = (enable: boolean) => {
    setPendingToggle(enable);
    setShowToggleDialog(true);
  };

  const confirmToggle = async () => {
    if (pendingToggle === null) return;

    setAutoProcessingStatus((prev) => ({ ...prev, loading: true }));
    try {
      const action = pendingToggle ? 'enable' : 'disable';
      const { data, error } = await supabase.functions.invoke('setup-auto-processing', {
        body: { action },
      });

      if (error) throw error;

      if (data?.success) {
        await checkAutoProcessingStatus();
        showSuccess(`Auto-processing ${pendingToggle ? 'enabled' : 'disabled'} successfully`);
      }
    } catch (err: any) {
      console.error('Error toggling auto-processing:', err);
      showError(err.message || 'Failed to toggle auto-processing');
    } finally {
      setAutoProcessingStatus((prev) => ({ ...prev, loading: false }));
      setShowToggleDialog(false);
      setPendingToggle(null);
    }
  };

  const handleManualProcessing = async () => {
    setManualProcessing(true);
    try {
      const { error } = await supabase.functions.invoke('process-law-notifications', {
        body: {},
      });
      if (error) throw error;
      const timestamp = new Date().toISOString();
      setLastManualRun(timestamp);
      showSuccess('Manual processing completed successfully');
    } catch (err: any) {
      console.error('Error running manual processing:', err);
      showError(err.message || 'Failed to process law notifications');
    } finally {
      setManualProcessing(false);
    }
  };

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="mb-4 sm:mb-6">
        <h1 className="text-2xl sm:text-3xl font-black text-luna-text-light dark:text-luna-text-dark mb-2">
          System Settings
        </h1>
        <p className="text-sm sm:text-base text-luna-text-light/70 dark:text-luna-text-dark/70">
          Configure system settings and automation
        </p>
      </div>

      <div className="bg-white dark:bg-luna-primary rounded-xl border-2 border-luna-secondary/30 p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 text-luna-text-light dark:text-luna-text-dark">
          Auto-Processing
        </h2>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div className="flex-1">
            <p className="text-sm sm:text-base text-luna-text-light dark:text-luna-text-dark mb-1">
              Automatic Database Updates
            </p>
            <p className="text-xs sm:text-sm text-luna-text-light/70 dark:text-luna-text-dark/70">
              Automatically process RSS feeds and send notifications
            </p>
          </div>
          <div className="flex items-center gap-3 sm:gap-4">
            {autoProcessingStatus.loading ? (
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-luna-secondary"></div>
            ) : (
              <>
                <span
                  className={`px-3 py-1.5 sm:py-1 rounded text-xs sm:text-sm font-medium whitespace-nowrap ${
                    autoProcessingStatus.enabled
                      ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                      : 'bg-gray-100 text-gray-800 dark:bg-gray-900/30 dark:text-gray-400'
                  }`}
                >
                  {autoProcessingStatus.enabled ? 'Enabled' : 'Disabled'}
                </span>
                <button
                  onClick={() => handleToggleClick(!autoProcessingStatus.enabled)}
                  disabled={autoProcessingStatus.loading}
                  className={`px-4 py-2.5 sm:py-2 rounded-lg font-medium transition-colors min-h-[44px] sm:min-h-[40px] touch-manipulation ${
                    autoProcessingStatus.enabled
                      ? 'bg-red-500 text-white hover:bg-red-600 active:bg-red-700'
                      : 'bg-green-500 text-white hover:bg-green-600 active:bg-green-700'
                  } disabled:opacity-50 disabled:cursor-not-allowed`}
                >
                  {autoProcessingStatus.enabled ? 'Disable' : 'Enable'}
                </button>
              </>
            )}
          </div>
        </div>
      </div>

      <div className="bg-white dark:bg-luna-primary rounded-xl border-2 border-luna-secondary/30 p-4 sm:p-6 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h2 className="text-lg sm:text-xl font-bold text-luna-text-light dark:text-luna-text-dark">
              Manual Processing
            </h2>
            <p className="text-sm sm:text-base text-luna-text-light/70 dark:text-luna-text-dark/70">
              Trigger law notification processing immediately when you need changes reflected right away.
            </p>
          </div>
          <button
            onClick={handleManualProcessing}
            disabled={manualProcessing}
            className="px-4 py-2.5 sm:py-2 rounded-lg bg-luna-secondary text-white font-semibold hover:bg-luna-secondary/90 active:bg-luna-secondary/80 disabled:opacity-50 disabled:cursor-not-allowed min-h-[44px] sm:min-h-[40px]"
          >
            {manualProcessing ? 'Processing…' : 'Run Manual Sync'}
          </button>
        </div>
        <div className="rounded-lg border border-luna-secondary/20 bg-luna-secondary/5 dark:bg-luna-secondary/10 px-4 py-3 text-sm text-luna-text-light/80 dark:text-luna-text-dark/80">
          <p className="font-medium mb-1">Last manual run:</p>
          <p>{lastManualRun ? new Date(lastManualRun).toLocaleString() : 'Not run yet'}</p>
        </div>
      </div>

      <div className="bg-white dark:bg-luna-primary rounded-xl border-2 border-luna-secondary/30 p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 text-luna-text-light dark:text-luna-text-dark">
          Branding
        </h2>
        <div className="space-y-3">
          <p className="text-sm sm:text-base text-luna-text-light/70 dark:text-luna-text-dark/70 mb-3">
            Customize your application's branding elements including logo, favicon, and header images.
          </p>
          <button
            onClick={() => window.location.hash = '#/admin/branding'}
            className="w-full sm:w-auto px-4 py-2.5 rounded-lg bg-luna-secondary text-white font-medium hover:bg-luna-secondary/90 transition-colors min-h-[44px]"
          >
            Manage Branding
          </button>
        </div>
      </div>

      <div className="bg-white dark:bg-luna-primary rounded-xl border-2 border-luna-secondary/30 p-4 sm:p-6">
        <h2 className="text-lg sm:text-xl font-bold mb-3 sm:mb-4 text-luna-text-light dark:text-luna-text-dark">
          System Information
        </h2>
        <div className="space-y-2 text-xs sm:text-sm">
          <div className="flex justify-between gap-4">
            <span className="text-luna-text-light/70 dark:text-luna-text-dark/70">Version:</span>
            <span className="text-luna-text-light dark:text-luna-text-dark font-medium">2.0.0</span>
          </div>
          <div className="flex justify-between gap-4">
            <span className="text-luna-text-light/70 dark:text-luna-text-dark/70">
              Database:
            </span>
            <span className="text-luna-text-light dark:text-luna-text-dark font-medium">Supabase</span>
          </div>
        </div>
      </div>

      {/* Toggle Confirmation Dialog */}
      <AdminConfirmDialog
        isOpen={showToggleDialog}
        title={pendingToggle ? 'Enable Auto-Processing' : 'Disable Auto-Processing'}
        message={
          pendingToggle
            ? 'Are you sure you want to enable automatic RSS feed processing and notifications? This will start processing feeds automatically.'
            : 'Are you sure you want to disable automatic RSS feed processing? This will stop all automatic processing.'
        }
        confirmText={pendingToggle ? 'Enable' : 'Disable'}
        cancelText="Cancel"
        onConfirm={confirmToggle}
        onCancel={() => {
          setShowToggleDialog(false);
          setPendingToggle(null);
        }}
        variant={pendingToggle ? 'info' : 'warning'}
      />
    </div>
  );
};
